import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger, SplitText);

export function initAnimatedCopy() {
    const sections = document.querySelectorAll(".block-animated-copy");
    if (!sections.length) return;

    sections.forEach((section) => {
        const text = section.querySelector(".text-reveal");
        const content = section.querySelector(".animated-copy-content");
        if (!text || !content) return;

        const split = SplitText.create(text, { type: "words" });

        gsap.set(content, { autoAlpha: 0, y: 24 });

        // time-based fade (not scrubbed), paused until words finish
        const fadeIn = gsap.to(content, {
            autoAlpha: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
            paused: true,
        });

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: "top top",
                end: () => "+=" + window.innerHeight, // 100vh of scroll
                pin: true,
                scrub: 0.5,
                invalidateOnRefresh: true,
                // markers: true,
                onUpdate: (self) => {
                    // play once all words are revealed, reverse when scrolling back
                    if (self.progress === 1) fadeIn.play();
                    else if (fadeIn.progress() > 0 && !fadeIn.reversed()) fadeIn.reverse();
                },
            },
        });

        // words: neutral-400 -> black, one after another
        tl.fromTo(
            split.words,
            { color: "#a3a3a3" },
            { color: "#000", ease: "none", stagger: 1, duration: 1 }
        );
    });
}

document.addEventListener("DOMContentLoaded", initAnimatedCopy);








const SWITCH_OFFSET = "top 30%";
const FADE_DURATION = 0.6;

export function initScrollingWithVisuals() {
    const sections = document.querySelectorAll(".block-scrolling-with-visuals");
    if (!sections.length) return;

    const mm = gsap.matchMedia();

    // desktop only; mobile shows media inline, no animation
    mm.add("(min-width: 1024px)", () => {
        sections.forEach((section) => {
            const layers = section.querySelectorAll(".svm-layer");
            const items = section.querySelectorAll(".svm-item");
            if (!layers.length || layers.length !== items.length) return;

            const show = (index) => {
                layers.forEach((layer, i) => {
                    gsap.to(layer, {
                        opacity: i === index ? 1 : 0,
                        duration: FADE_DURATION,
                        ease: "power1.inOut",
                        overwrite: "auto",
                    });
                });
            };

            items.forEach((item, i) => {
                if (i === 0) return;

                ScrollTrigger.create({
                    trigger: item,
                    start: SWITCH_OFFSET,
                    // markers: true,
                    onEnter: () => show(i),
                    onLeaveBack: () => show(i - 1),
                });
            });
        });
    });
}

document.addEventListener("DOMContentLoaded", initScrollingWithVisuals);

















const CLOSED = "polygon(50% 0%, 50% 100%, 50% 100%, 50% 0%)";
const OPEN = "polygon(100% 0%, 100% 100%, 0% 100%, 0% 0%)";
const GROW_OPEN = 2.24;
const GROW_CLOSED = 1;

const TEXT_OUT = 0.12;      // old text fades out
const SHAPE_START = 0.05;   // shapes start almost immediately (overlaps the text fade)
const SHAPE = 0.42;         // clip-path + size
const TEXT_IN = 0.25;       // new text fades in
const TEXT_OVERLAP = 0.12;  // text starts this much before the shape finishes
const HOVER_INTENT = 30;    // ms
const EASE = "power2.inOut";

export function initInfoCards() {
    const wrappers = document.querySelectorAll(".block-info-cards .group\\/cards");
    if (!wrappers.length) return;

    const mm = gsap.matchMedia();

    mm.add("(min-width: 1024px)", () => {
        const cleanups = [];

        wrappers.forEach((wrapper) => {
            const cards = gsap.utils.toArray(".info-card", wrapper);
            if (!cards.length) return;

            // strip leftover CSS-driven sizing so only GSAP controls it
            cards.forEach((card) => {
                Array.from(card.classList)
                    .filter((c) => /grow-|^transition|^duration-/.test(c))
                    .forEach((c) => card.classList.remove(c));
            });

            const parts = cards.map((card) => ({
                card,
                content: card.querySelector(".info-card-content"),
                text:
                    card.querySelector(".info-card-text") ||
                    card.querySelector(".info-card-content > div"),
            }));

            const everything = parts.flatMap((p) => [p.card, p.content, p.text]);

            parts.forEach((p, i) => {
                gsap.set(p.card, {
                    transition: "none",
                    flexGrow: i === 0 ? GROW_OPEN : GROW_CLOSED,
                });
                gsap.set(p.content, { clipPath: i === 0 ? OPEN : CLOSED });
                gsap.set(p.text, { opacity: i === 0 ? 1 : 0 });
            });

            let active = 0;

            // One transition describes the target for EVERY card, always built from
            // the current values, so fast hovering can never leave a card half-open.
            const goTo = (index) => {
                if (index === active) return;
                active = index;

                gsap.killTweensOf(everything);

                const tl = gsap.timeline();

                parts.forEach((p, i) => {
                    if (i === index) return;
                    // closing: text first, then clip-path + size
                    tl.to(p.text, { opacity: 0, duration: TEXT_OUT, ease: "power1.out" }, 0)
                        .to(p.card, { flexGrow: GROW_CLOSED, duration: SHAPE, ease: EASE }, SHAPE_START)
                        .to(p.content, { clipPath: CLOSED, duration: SHAPE, ease: EASE }, SHAPE_START);
                });

                // opening: clip-path + size in sync with the closing, then text
                const a = parts[index];
                tl.to(a.card, { flexGrow: GROW_OPEN, duration: SHAPE, ease: EASE }, SHAPE_START)
                    .to(a.content, { clipPath: OPEN, duration: SHAPE, ease: EASE }, SHAPE_START)
                    .to(
                        a.text,
                        { opacity: 1, duration: TEXT_IN, ease: "power1.out" },
                        SHAPE_START + SHAPE - TEXT_OVERLAP
                    );
            };

            let timer;
            parts.forEach((p, i) => {
                const onEnter = () => {
                    clearTimeout(timer);
                    timer = setTimeout(() => goTo(i), HOVER_INTENT);
                };
                p.card.addEventListener("mouseenter", onEnter);
                cleanups.push(() => p.card.removeEventListener("mouseenter", onEnter));
            });

            // last hovered card stays active, no reset on mouseleave
            cleanups.push(() => clearTimeout(timer));
        });

        return () => cleanups.forEach((fn) => fn());
    });
}

document.addEventListener("DOMContentLoaded", initInfoCards);